import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LeapV2Ilp } from '../models/leap-v2-ilp.model';
import { LeapV2UploadedFile } from '../models/leap-v2-student.model';
import { LeapV2IlpService } from '../services/leap-v2-ilp.service';
import { LeapV2EvidenceService } from '../services/leap-v2-evidence.service';
import { LeapV2EvidenceUploadComponent } from '../leap-v2-evidence-upload/leap-v2-evidence-upload.component';
import { LeapV2DebugDialogComponent } from '../dialogs/leap-v2-debug-dialog/leap-v2-debug-dialog.component';
import { TutorReportsHelperService } from '../../../../_helpers/tutor-reports-helper/tutor-reports-helper.service';
import { FeatureHelperService } from '../../../../_helpers/feature-helper/feature-helper.service';
import { ErrorModalHelperService } from '../../../../_helpers/error-modal-helper/error-modal-helper.service';
import { SuccessModalHelperService } from '../../../../_helpers/success-modal-helper/success-modal-helper.service';
import { DynamicConfirmModalComponent } from '../../../../shared/dynamic-confirm-modal/dynamic-confirm-modal.component';
import { SubmissionHistoryDialog } from '../../../../shared/submission-history-dialog';
import { SEMH_LABELS, SEMH_MIN, SEMH_MAX } from '../models/leap-v2-semh';
import { KeyStage } from '../../../../_models/tutor-reports/tutorreport';
import { hasRole } from '../../../../_helpers/can-use/current-user-roles';
import { returnToPreviousPage } from '../../../../_helpers/navigation/return-to-previous-page';
import { PlacementService } from '../../../../_services/placementservice';
import { PlacementData } from '../../../../_models';
import { PlacementMaintenance } from '../../../../_models/maintenance/placement-maintenance.model';

@Component({
  selector: 'leap-v2-ilp',
  templateUrl: './leap-v2-ilp.component.html',
  styleUrls: ['./leap-v2-ilp.component.css'],
  standalone: false,
})
export class LeapV2IlpComponent implements OnInit, OnDestroy {
  // The booking summary header; AppNewBaseComponent picks this template up for its utility bar.
  @ViewChild('utilityBarForm', { static: true }) utilityBarForm: TemplateRef<any>;

  // Populated by the load call in ngOnInit; the template guards on this being set.
  ilp: LeapV2Ilp;

  // Documents already uploaded to the plan, as the server last returned them. ilp.evidence is these plus
  // the files picked since.
  savedEvidence: LeapV2UploadedFile[] = [];
  @ViewChild(LeapV2EvidenceUploadComponent) evidenceUpload: LeapV2EvidenceUploadComponent;
  // The files picked since load, as the upload control last emitted them.
  private picked: LeapV2UploadedFile[] = [];

  placementData: PlacementData;
  placementMaintenance: PlacementMaintenance;
  private unsubscribe: Subject<void> = new Subject<void>();

  // Debug shows the raw model, so admins only.
  readonly canDebug = hasRole('ADMIN');
  readonly maxChars = 1000;
  // The plan holds a migrated ILP's STEPS brief and target together (39881), so it takes more. Same limit as the API's Ilp.PlanMaxLength.
  readonly planMaxChars = 4000;
  // Areas of strength holds a migrated ILP's STEPS initial assessment (up to 1,024). Same limit as Ilp.AreasOfStrengthMaxLength.
  readonly areasOfStrengthMaxChars = 4000;
  // The width of tblBaseline.AssessmentUsed. Same limit as Ilp.AssessmentUsedMaxLength.
  readonly assessmentUsedMaxChars = 200;
  // dbo.tblKeyStage rows: the option values are their Ids, which tblBaseline.InitialKeyStage references.
  keyStages: KeyStage[] = [];
  readonly percentTicks = ['0%', '25%', '50%', '75%', '100%'];
  readonly semhLabels = SEMH_LABELS;
  readonly semhMin = SEMH_MIN;
  readonly semhMax = SEMH_MAX;

  placementId: number;
  studentId: number;

  constructor(
    private cdr: ChangeDetectorRef,
    private ilpService: LeapV2IlpService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private ngZone: NgZone,
    private featureHelperService: FeatureHelperService,
    private errorModalHelper: ErrorModalHelperService,
    private tutorReportsHelperService: TutorReportsHelperService,
    private placementService: PlacementService,
    private successModalHelper: SuccessModalHelperService,
    private evidenceService: LeapV2EvidenceService
  ) { }

  /** Opens an uploaded document through the API (its link is api/Evidence/{id}, not a blob URL). */
  openEvidence(event: Event, file: LeapV2UploadedFile): void {
    event.preventDefault();
    this.evidenceService.open(file);
  }

  openDebugDialog(): void {
    this.dialog.open(LeapV2DebugDialogComponent, {
      width: '600px',
      data: { title: 'ILP model', model: this.ilp },
    });
  }

  ngOnInit(): void {
    // Ids come from the route (/individual-learning-plan/:placementId/:studentId) so the screen is deep-linkable.
    this.placementId = Number(this.route.snapshot.paramMap.get('placementId'));
    this.studentId = Number(this.route.snapshot.paramMap.get('studentId'));
    this.tutorReportsHelperService.ensureContext(this.placementId);
    this.loadPlacementHeader();

    // A booking that is not found or not the user's reads as one plain message, not the support modal (40973).
    this.ilpService.loadIlp(this.placementId, this.studentId, true).subscribe({
      next: ilp => {
        this.ilp = ilp;
        this.savedEvidence = savedEvidenceOf(ilp);
        this.cdr.detectChanges();
      },
      error: error => this.errorModalHelper.show(error.message, error)
    });

    this.ilpService.loadKeyStages(this.placementId, this.studentId, true).subscribe({
      next: keyStages => {
        this.keyStages = keyStages ?? [];
        this.cdr.detectChanges();
      },
      error: error => this.errorModalHelper.show(error.message, error)
    });
  }

  /**
   * A submitted (or approved) ILP is locked: every control is read-only/disabled and it can't be
   * saved or submitted again until a consultant unlocks it. statusId 2 = Submitted, 3 = Approved.
   */
  get isLocked(): boolean {
    return !!this.ilp
      && (this.ilp.approved === true
        || this.ilp.status === 'Approved'
        || this.ilp.status === 'Submitted'
        || this.ilp.statusId === 2
        || this.ilp.statusId === 3);
  }

  // Submitted is the only state Accept/Reject apply to: the tutor is done and QA has not signed off yet.
  get reportSubmitted(): boolean {
    return !!this.ilp && this.ilp.status === 'Submitted';
  }

  // Approved is terminal for sign-off — no Save/Submit and no Accept/Reject. Unlock is the way back.
  get reportFinalised(): boolean {
    return !!this.ilp && this.ilp.status === 'Approved';
  }

  // The ILP has its own approve/reject features — deliberately not the session report's TutorReports*
  // pair. Same gating as the ILP rows on the QA approval grid; the API enforces it too, this only
  // hides UI.
  //
  // Do NOT gate this on `isStaff`. That flag means "holds the STAFF role code", and STAFF is the
  // TUTOR role here — using it shows Accept to tutors and hides it from admins.
  get canApprove(): boolean {
    return this.featureHelperService.hasFeature('IndividualLearningReportsApprove');
  }

  get canReject(): boolean {
    return this.featureHelperService.hasFeature('IndividualLearningReportsReject');
  }

  // Drives the footer branch: the Accept/Reject row shows when the user can do either. A tutor holds
  // neither feature and so keeps the Save/Submit footer they see today.
  get canAuthorise(): boolean {
    return this.canApprove || this.canReject;
  }

  /**
   * The uploaded documents the screen lists. One the tutor has attached again under the same name is left out:
   * the save stores the new copy in its place, so it is listed once, in the picker.
   */
  get shownSavedEvidence(): LeapV2UploadedFile[] {
    return this.savedEvidence.filter(s => !this.picked.some(p => sameName(p, s)));
  }

  // The upload control emits only the files picked since load, so keep the uploaded ones alongside them. They go
  // back to the API by url and are not stored again. The API stores exactly this list (40454): an uploaded
  // document it no longer contains is removed.
  onEvidenceChanged(files: LeapV2UploadedFile[]): void {
    this.picked = files ?? [];
    this.ilp.evidence = [...this.shownSavedEvidence, ...this.picked];
  }

  /**
   * Removes an uploaded document from a draft plan once the tutor confirms (40454). Only the screen changes
   * here: the next save sends the list without it, and the API removes it then. A submitted or approved plan
   * keeps its documents.
   */
  removeSavedEvidence(file: LeapV2UploadedFile): void {
    if (this.isLocked) return;
    this.dialog.open(DynamicConfirmModalComponent, {
      width: '440px',
      data: { confirmMessage: `Remove ${file.name} from the assessment evidence?`, confirmLabel: 'Remove' },
    })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.savedEvidence = this.savedEvidence.filter(s => s !== file);
        this.onEvidenceChanged(this.picked);
        this.cdr.detectChanges();
      });
  }

  saveIlp(): void {
    if (this.isLocked) return;
    this.ilpService.saveIlp(this.ilp).subscribe({
      // Say so: the screen does not change on a draft save. Then re-read, as submit does, so the
      // status and the saved evidence come from the server record.
      next: () => {
        this.successModalHelper.show('Individual learning plan saved. Submit it when it is complete.');
        this.reload();
      },
      error: (error) => this.errorModalHelper.show(error.message, error),
    });
  }

  // The initial key stage is compulsory to submit, as it was in STEPS. A draft can still be saved without it.
  get keyStageMissing(): boolean {
    return !!this.ilp && this.ilp.academic.initialKeyStage == null;
  }

  submitIlp(): void {
    if (this.isLocked || this.keyStageMissing) return;
    this.ilpService.submitIlp(this.ilp).subscribe({
      // Re-read rather than assume: the status submit lands on is brand-conditional — FES goes to
      // Submitted for QA, everyone else auto-approves — so hard-coding Approved here would label an
      // FES ILP wrongly. Both statuses lock the screen, so only the badge was ever at stake.
      next: () => this.reload(),
      // A refused submit (422, e.g. a file that could not be read) is not shown by the error interceptor.
      error: (error) => this.errorModalHelper.show(error.message, error),
    });
  }

  /**
   * Accept — QA sign-off, Submitted to Approved. Same endpoint the QA approval grid calls; the plan
   * becomes visible to the client.
   */
  acceptIlp(): void {
    this.dialog.open(DynamicConfirmModalComponent, {
      width: '440px',
      data: { confirmMessage: 'Approve this individual learning plan? It becomes visible to the client.' },
    })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;

        this.ilpService.authoriseIlp(this.ilp.rowId).subscribe({
          next: () => this.reload(),
          error: (error) => this.errorModalHelper.show(error.message, error),
        });
      });
  }

  /**
   * Reject — sends the plan back to the tutor with a mandatory reason, Submitted to Rejected. Rejected
   * is not terminal: the screen unlocks so the tutor can correct and resubmit.
   *
   * Reuses the legacy SubmissionHistoryDialog (20–200 chars). Its Cancel and close-X both emit `true`
   * from afterClosed(), so only the onConfirmed emit counts as a confirmation.
   */
  rejectIlp(): void {
    const dialogRef = this.dialog.open(SubmissionHistoryDialog, {
      panelClass: 'my-dialog',
      data: { title: 'Reject learning plan', label: 'Reason for rejection (emailed to the tutor when rejection emails are switched on)' },
    });
    const confirmed = dialogRef.componentInstance.onConfirmed.subscribe((reason: string) => {
      dialogRef.close();
      this.ilpService.rejectIlp(this.ilp.rowId, reason).subscribe({
        next: () => this.reload(),
        error: (error) => this.errorModalHelper.show(error.message, error),
      });
    });
    dialogRef.afterClosed().subscribe(() => confirmed.unsubscribe());
  }

  unlockIlp(): void {
    this.ilpService.unlockIlp(this.placementId, this.studentId).subscribe(() => {
      this.ilp.approved = false;
      this.ilp.status = 'Draft';
      this.ilp.statusId = 1; // Saved — back to editable so the lock lifts.
      this.cdr.detectChanges();
    });
  }

  backToProfile(): void {
    returnToPreviousPage(this.router, this.placementId);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  /** Feeds the booking summary header from the shared placement context, as View Sessions does. */
  private loadPlacementHeader(): void {
    this.tutorReportsHelperService.placementData$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(data => this.placementData = data);
    this.tutorReportsHelperService.placementMaintenance$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(maintenance => this.placementMaintenance = maintenance);

    this.tutorReportsHelperService.loadPlacementHeader(this.placementId, this.placementService,
      error => this.errorModalHelper.show(error.message, error));
  }

  /**
   * Re-reads the ILP so the status pill, the lock and the footer all come from the server record
   * rather than a guess. ngZone.run because the dialog callbacks these run from can land outside
   * Angular's zone.
   */
  private reload(): void {
    this.ilpService.loadIlp(this.placementId, this.studentId).subscribe(saved => {
      this.ngZone.run(() => {
        this.ilp = saved;
        this.savedEvidence = savedEvidenceOf(saved);
        // The picked files are uploaded now and listed above; leaving them in the control would
        // upload them again on the next save.
        this.picked = [];
        if (this.evidenceUpload) this.evidenceUpload.files = [];
        this.cdr.detectChanges();
      });
    });
  }
}

/** The plan's uploaded documents: the evidence the server returned with a link to open. */
function savedEvidenceOf(ilp: LeapV2Ilp): LeapV2UploadedFile[] {
  return (ilp?.evidence ?? []).filter(f => !!f.url);
}

/** Same file name, ignoring case, as the API matches them. */
function sameName(a: LeapV2UploadedFile, b: LeapV2UploadedFile): boolean {
  return (a?.name ?? '').toLowerCase() === (b?.name ?? '').toLowerCase();
}
